import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { SelectionPreview } from './SelectionPreview';
import { matchesQuery } from './selectionKind';
import { SEL_API, shareSelection } from '../../lib/selectionShare';

const eur = (n) => Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Stat = ({ label, played, net, testid }) => (
  <div className="flex-1 rounded-xl bg-[#161616] border border-white/5 p-3" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">{label}</p>
    <p className="text-xs text-gray-400">Importo <span className="text-white font-bold" data-testid={`${testid}-amount`}>{eur(played)}</span></p>
    <p className="text-xs text-gray-400">Rendimento <span className={`font-bold ${net > 0 ? 'text-[#22C55E]' : net < 0 ? 'text-red-400' : 'text-gray-200'}`} data-testid={`${testid}-return`}>{net > 0 ? '+' : ''}{eur(net)}</span></p>
  </div>
);

// "Diario di gioco consapevole": finished selections, weekly/monthly totals, personal weekly limit
export const DiaryView = ({ query = '', reloadKey, onEdit }) => {
  const [d, setD] = useState(null);
  const [limit, setLimit] = useState('');

  const load = useCallback(() => axios.get(`${SEL_API}/diary`).then((r) => { setD(r.data); setLimit(r.data.weekly_limit ? String(r.data.weekly_limit) : ''); }).catch(() => setD({ selections: [] })), []);
  useEffect(() => { load(); }, [load, reloadKey]);
  const [states, setStates] = useState({});
  const liveCount = (d?.selections || []).filter((s) => s.live).length;
  useEffect(() => {
    if (!liveCount) return undefined;
    const tick = () => axios.get(`${SEL_API}/live`, { timeout: 20000 }).then((r) => setStates(r.data || {})).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [liveCount]);

  const saveLimit = async () => {
    try {
      const v = parseFloat(String(limit).replace(',', '.'));
      await axios.put(`${SEL_API}/diary/limit`, { weekly_limit: Number.isFinite(v) && v > 0 ? v : null });
      toast.success('Limite salvato');
      load();
    } catch { toast.error('Salvataggio non riuscito'); }
  };

  const remove = useCallback(async (sel) => {
    if (!window.confirm('Eliminare questa selezione dal diario?')) return;
    await axios.delete(`${SEL_API}/${sel.selection_id}`).catch(() => {});
    load();
  }, [load]);

  if (!d) return <Loader2 className="w-6 h-6 animate-spin text-[#F5A623] mx-auto" />;
  return (
    <div data-testid="diary-view">
      <h3 className="text-white font-black text-base mb-3">Diario di gioco consapevole</h3>
      {d.week && (
        <div className="flex gap-2 mb-3">
          <Stat label="Questa settimana" played={d.week.played} net={d.week.net} testid="diary-week" />
          <Stat label="Questo mese" played={d.month.played} net={d.month.net} testid="diary-month" />
        </div>
      )}
      {d.spend_up && (
        <p className="mb-2 rounded-xl bg-[#F5A623]/10 border border-[#F5A623]/40 text-[#F5A623] text-xs p-3 flex gap-2" data-testid="diary-spend-up">
          <span>📊 Avviso di tracciamento: il volume degli importi registrati questa settimana è superiore del 30% rispetto alla tua media mensile. <a href="/gioco-responsabile" className="underline font-bold" data-testid="diary-rg-link">Consulta le risorse sul gioco responsabile.</a></span>
        </p>
      )}
      {d.over_limit && (
        <p className="mb-2 rounded-xl bg-red-500/10 border border-red-500/40 text-red-300 text-xs p-3 flex gap-2" data-testid="diary-over-limit">
          <span>⚠️ Attenzione: hai superato il limite di spesa settimanale da te impostato ({eur(d.weekly_limit)}). Ti invitiamo a monitorare le tue attività nel rispetto del gioco responsabile.</span>
        </p>
      )}
      <div className="flex items-end gap-2 mb-5">
        <label className="flex-1">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Il mio limite di spesa settimanale</span>
          <input inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="Nessun limite"
            className="w-full bg-[#111] border border-white/10 rounded-xl px-3 py-2 text-white font-bold text-sm outline-none focus:border-[#F5A623]/60" data-testid="diary-limit-input" />
        </label>
        <button type="button" onClick={saveLimit} className="px-4 py-2 rounded-xl bg-[#F5A623] text-[#1A1A1A] text-sm font-black" data-testid="diary-limit-save">Salva</button>
      </div>
      {d.selections.length === 0 && <p className="text-center text-gray-500 text-sm py-6" data-testid="diary-empty">Nessuna selezione conclusa.</p>}
      {d.selections.length > 0 && !d.selections.some((s) => matchesQuery(s, query)) && <p className="text-center text-gray-500 text-sm py-6" data-testid="diary-no-match">Nessuna selezione trovata.</p>}
      <div className="space-y-3">{d.selections.filter((s) => matchesQuery(s, query)).map((s) => <SelectionPreview key={s.selection_id} sel={s} states={states[s.selection_id]} onShare={(x) => shareSelection(x)} onDelete={remove} onEdit={onEdit} />)}</div>
    </div>
  );
};

export default DiaryView;
